import React, { useState, useCallback } from 'react';
import { useAIFeatures } from '../hooks/useAIFeatures';
import { RefreshCw, AlertTriangle, TrendingUp, CheckCircle, Cpu, Shield,
         ChevronDown, ChevronRight, Zap, BarChart2, Package, Clock, Printer } from 'lucide-react';
import api from '../api/client';
import { PageHeader } from '../components/FilterBar';

const HEALTH_COLOR = { good:'#10b981', fair:'#f59e0b', warning:'#f97316', critical:'#ef4444', unknown:'#475569' };
const SEV_COLOR    = { high:'#ef4444', medium:'#f97316', low:'#f59e0b' };
const SEV_BG       = { high:'rgba(239,68,68,0.08)', medium:'rgba(249,115,22,0.08)', low:'rgba(245,158,11,0.08)' };
const CAT_ICON     = { asset:<Cpu size={13}/>, employee:<Shield size={13}/>, license:<Zap size={13}/>,
                       ticket:<Clock size={13}/>, toner:<Printer size={13}/>, security:<Shield size={13}/> };

function DisabledMsg({ feature }) {
  return (
    <div className="py-12 text-center">
      <div className="text-2xl mb-2">🔒</div>
      <div className="text-sm font-medium" style={{ color:'#475569' }}>{feature} is disabled</div>
      <div className="text-xs mt-1" style={{ color:'#334155' }}>An admin can enable it under Settings → AI Features</div>
    </div>
  );
}

function fmtLKR(n) {
  if (!n) return '—';
  return 'LKR ' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function HealthBadge({ level }) {
  const col = HEALTH_COLOR[level] || '#475569';
  const labels = { good:'Healthy', fair:'Fair', warning:'Warning', critical:'Critical', unknown:'Unknown' };
  return (
    <span className="px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{ background: `${col}20`, color: col, border: `1px solid ${col}40` }}>
      {labels[level] || level}
    </span>
  );
}

function AnomalyCard({ item }) {
  const [open, setOpen] = useState(false);
  const col = SEV_COLOR[item.severity] || '#475569';
  const bg  = SEV_BG[item.severity]   || 'rgba(255,255,255,0.03)';
  return (
    <div className="rounded-xl overflow-hidden" style={{ background: bg, border: `1px solid ${col}25` }}>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left">
        <span style={{ color: col }}>{CAT_ICON[item.category] || <AlertTriangle size={13}/>}</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium" style={{ color: '#e2e8f0' }}>{item.title}</div>
          <div className="text-xs mt-0.5 truncate" style={{ color: '#64748b' }}>{item.affected}</div>
        </div>
        <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0"
          style={{ background: `${col}20`, color: col }}>
          {item.severity}
        </span>
        {open ? <ChevronDown size={13} style={{ color:'#475569', flexShrink:0 }}/> : <ChevronRight size={13} style={{ color:'#475569', flexShrink:0 }}/>}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2 text-xs border-t" style={{ borderColor: `${col}20` }}>
          <div className="pt-3" style={{ color: '#94a3b8' }}>{item.description}</div>
          <div className="flex items-start gap-2 p-2.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
            <CheckCircle size={11} className="mt-0.5 flex-shrink-0" style={{ color:'#10b981' }}/>
            <span style={{ color:'#34d399' }}><strong>Action: </strong>{item.recommendation}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Forecast Tab ──────────────────────────────────────────────────────────────
function ForecastTab() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/ai/asset-forecast');
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to generate forecast');
    } finally { setLoading(false); }
  }, []);

  const f = data?.forecast;
  const riskCol = HEALTH_COLOR[f?.overall_risk] || '#475569';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium" style={{ color:'#94a3b8' }}>
            AI analyses age, repair history and costs to identify assets needing replacement
          </div>
        </div>
        <button onClick={load} disabled={loading}
          className="btn-primary flex items-center gap-2"
          style={{ opacity: loading ? 0.7 : 1 }}>
          {loading ? <RefreshCw size={13} className="animate-spin"/> : <TrendingUp size={13}/>}
          {loading ? 'Analysing...' : data ? 'Refresh' : 'Generate Forecast'}
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-xl text-xs flex items-center gap-2"
          style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', color:'#f87171' }}>
          <AlertTriangle size={13}/>{error}
        </div>
      )}

      {!data && !loading && (
        <div className="rounded-2xl p-12 text-center" style={{ background:'rgba(255,255,255,0.02)', border:'1px dashed rgba(255,255,255,0.08)' }}>
          <TrendingUp size={32} className="mx-auto mb-3" style={{ color:'#1e3a5f' }}/>
          <div className="text-sm font-medium mb-1" style={{ color:'#334155' }}>Asset Replacement Forecast</div>
          <div className="text-xs" style={{ color:'#1e3a5f' }}>
            Click "Generate Forecast" to analyse your asset inventory
          </div>
        </div>
      )}

      {f && (
        <div className="space-y-4">
          {/* Summary card */}
          <div className="rounded-2xl p-5" style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-semibold" style={{ color:'#60a5fa' }}>EXECUTIVE SUMMARY</div>
              <span className="text-xs px-2.5 py-1 rounded-full font-semibold"
                style={{ background:`${riskCol}20`, color: riskCol, border:`1px solid ${riskCol}40` }}>
                Risk: {f.overall_risk}
              </span>
            </div>
            <p className="text-sm" style={{ color:'#cbd5e1', lineHeight:1.6 }}>{f.summary}</p>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label:'Critical Replacements', val: f.total_critical_count || f.critical?.length || 0, col:'#ef4444' },
              { label:'Planned (12 months)', val: f.planned_12_months?.length || 0, col:'#f97316' },
              { label:'Total Budget Estimate', val: fmtLKR(f.total_budget_lkr), col:'#10b981' },
            ].map(s => (
              <div key={s.label} className="rounded-xl p-4 text-center"
                style={{ background:`${s.col}10`, border:`1px solid ${s.col}25` }}>
                <div className="text-xl font-bold" style={{ color: s.col }}>{s.val}</div>
                <div className="text-xs mt-1" style={{ color:'#475569' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Budget by category */}
          {f.budget_by_category && Object.keys(f.budget_by_category).some(k => f.budget_by_category[k] > 0) && (
            <div className="rounded-xl p-4" style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)' }}>
              <div className="text-xs font-semibold mb-3" style={{ color:'#94a3b8' }}>BUDGET BY CATEGORY</div>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(f.budget_by_category).filter(([,v]) => v > 0).map(([k,v]) => (
                  <div key={k} className="text-xs">
                    <span style={{ color:'#475569' }}>{k.charAt(0).toUpperCase()+k.slice(1)}: </span>
                    <span className="font-mono" style={{ color:'#60a5fa' }}>{fmtLKR(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Critical assets */}
          {f.critical?.length > 0 && (
            <div>
              <div className="text-xs font-semibold mb-2 flex items-center gap-1.5" style={{ color:'#f87171' }}>
                <AlertTriangle size={11}/> CRITICAL — Replace Now ({f.critical.length})
              </div>
              <div className="space-y-2">
                {f.critical.map((a, i) => (
                  <div key={i} className="rounded-xl px-4 py-3 flex items-center gap-3 text-xs"
                    style={{ background:'rgba(239,68,68,0.07)', border:'1px solid rgba(239,68,68,0.2)' }}>
                    <div className="flex-1">
                      <span className="font-mono font-semibold" style={{ color:'#f87171' }}>{a.asset_number}</span>
                      <span className="mx-2" style={{ color:'#334155' }}>·</span>
                      <span style={{ color:'#94a3b8' }}>{a.brand_model || `${a.category}`}</span>
                      <span className="mx-2" style={{ color:'#334155' }}>·</span>
                      <span style={{ color:'#475569' }}>{a.company}</span>
                    </div>
                    <div style={{ color:'#64748b' }}>{a.age_years}y old{a.repair_count > 0 ? ` · ${a.repair_count} repairs` : ''}</div>
                    <div className="font-mono" style={{ color:'#10b981' }}>{fmtLKR(a.estimated_cost_lkr)}</div>
                    <div className="max-w-xs text-right" style={{ color:'#475569' }}>{a.reason}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Planned */}
          {f.planned_12_months?.length > 0 && (
            <div>
              <div className="text-xs font-semibold mb-2 flex items-center gap-1.5" style={{ color:'#f97316' }}>
                <Clock size={11}/> PLAN FOR NEXT 12 MONTHS ({f.planned_12_months.length})
              </div>
              <div className="space-y-1.5">
                {f.planned_12_months.map((a, i) => (
                  <div key={i} className="rounded-xl px-4 py-2.5 flex items-center gap-3 text-xs"
                    style={{ background:'rgba(249,115,22,0.06)', border:'1px solid rgba(249,115,22,0.15)' }}>
                    <span className="font-mono font-semibold" style={{ color:'#fb923c' }}>{a.asset_number}</span>
                    <span style={{ color:'#94a3b8' }}>{a.brand_model || a.category}</span>
                    <span style={{ color:'#475569' }}>{a.company}</span>
                    <div className="flex-1"/>
                    <span style={{ color:'#64748b' }}>{a.age_years}y · {a.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {f.recommendations?.filter(Boolean).length > 0 && (
            <div className="rounded-xl p-4 space-y-2"
              style={{ background:'rgba(16,185,129,0.06)', border:'1px solid rgba(16,185,129,0.2)' }}>
              <div className="text-xs font-semibold" style={{ color:'#34d399' }}>AI RECOMMENDATIONS</div>
              {f.recommendations.filter(Boolean).map((r, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <CheckCircle size={11} className="mt-0.5 flex-shrink-0" style={{ color:'#10b981' }}/>
                  <span style={{ color:'#94a3b8' }}>{r}</span>
                </div>
              ))}
            </div>
          )}

          <div className="text-xs text-right" style={{ color:'#1e3a5f' }}>
            Analysis based on {data.assetCount} assets · Generated {new Date(data.generatedAt).toLocaleString('en-GB')}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Anomalies Tab ─────────────────────────────────────────────────────────────
function AnomaliesTab() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/ai/anomalies');
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to run anomaly detection');
    } finally { setLoading(false); }
  }, []);

  const high   = data?.anomalies?.filter(a => a.severity === 'high')   || [];
  const medium = data?.anomalies?.filter(a => a.severity === 'medium') || [];
  const low    = data?.anomalies?.filter(a => a.severity === 'low')    || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium" style={{ color:'#94a3b8' }}>
          AI scans for repeated repairs, damage patterns, expiring licenses, overdue tickets and more
        </div>
        <button onClick={load} disabled={loading}
          className="btn-primary flex items-center gap-2"
          style={{ opacity: loading ? 0.7 : 1 }}>
          {loading ? <RefreshCw size={13} className="animate-spin"/> : <BarChart2 size={13}/>}
          {loading ? 'Scanning...' : data ? 'Re-Scan' : 'Run Detection'}
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-xl text-xs flex items-center gap-2"
          style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', color:'#f87171' }}>
          <AlertTriangle size={13}/>{error}
        </div>
      )}

      {!data && !loading && (
        <div className="rounded-2xl p-12 text-center" style={{ background:'rgba(255,255,255,0.02)', border:'1px dashed rgba(255,255,255,0.08)' }}>
          <AlertTriangle size={32} className="mx-auto mb-3" style={{ color:'#1e3a5f' }}/>
          <div className="text-sm font-medium mb-1" style={{ color:'#334155' }}>Anomaly Detection</div>
          <div className="text-xs" style={{ color:'#1e3a5f' }}>
            Click "Run Detection" to scan for operational anomalies and risks
          </div>
        </div>
      )}

      {data && (
        <div className="space-y-4">
          {/* Health summary */}
          <div className="rounded-2xl p-5" style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-semibold" style={{ color:'#60a5fa' }}>SYSTEM HEALTH</div>
              <HealthBadge level={data.overall_health}/>
            </div>
            <p className="text-sm" style={{ color:'#cbd5e1', lineHeight:1.6 }}>{data.summary}</p>
          </div>

          {data.anomalies?.length === 0 && (
            <div className="rounded-xl p-6 text-center" style={{ background:'rgba(16,185,129,0.06)', border:'1px solid rgba(16,185,129,0.2)' }}>
              <CheckCircle size={24} className="mx-auto mb-2" style={{ color:'#10b981' }}/>
              <div className="text-sm font-medium" style={{ color:'#34d399' }}>All Clear</div>
              <div className="text-xs mt-1" style={{ color:'#475569' }}>No anomalies detected</div>
            </div>
          )}

          {/* Stats */}
          {data.anomalies?.length > 0 && (
            <div className="grid grid-cols-3 gap-3">
              {[
                { label:'High Priority', count: high.length,   col:'#ef4444' },
                { label:'Medium',        count: medium.length, col:'#f97316' },
                { label:'Low',           count: low.length,    col:'#f59e0b' },
              ].map(s => (
                <div key={s.label} className="rounded-xl p-3 text-center"
                  style={{ background:`${s.col}10`, border:`1px solid ${s.col}25` }}>
                  <div className="text-2xl font-bold" style={{ color: s.col }}>{s.count}</div>
                  <div className="text-xs mt-0.5" style={{ color:'#475569' }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Grouped anomaly cards */}
          {high.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-semibold" style={{ color:'#f87171' }}>HIGH PRIORITY</div>
              {high.map(a => <AnomalyCard key={a.id} item={a}/>)}
            </div>
          )}
          {medium.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-semibold" style={{ color:'#fb923c' }}>MEDIUM</div>
              {medium.map(a => <AnomalyCard key={a.id} item={a}/>)}
            </div>
          )}
          {low.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-semibold" style={{ color:'#fbbf24' }}>LOW</div>
              {low.map(a => <AnomalyCard key={a.id} item={a}/>)}
            </div>
          )}

          <div className="text-xs text-right" style={{ color:'#1e3a5f' }}>
            Generated {new Date(data.generatedAt).toLocaleString('en-GB')}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AIInsights() {
  const [tab, setTab] = useState('anomalies');
  const aiFeatures = useAIFeatures();

  return (
    <div className="space-y-4">
      <PageHeader title="AI Insights">
        <span className="text-xs px-2.5 py-1 rounded-full font-medium"
          style={{ background:'rgba(139,92,246,0.15)', color:'#a78bfa', border:'1px solid rgba(139,92,246,0.3)' }}>
          ✦ Powered by Claude AI
        </span>
      </PageHeader>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl" style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', width:'fit-content' }}>
        {[
          { id:'anomalies', label:'🔍 Anomaly Detection' },
          { id:'forecast',  label:'📈 Asset Forecast' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
            style={{
              background: tab === t.id ? 'rgba(139,92,246,0.2)' : 'transparent',
              color: tab === t.id ? '#a78bfa' : '#475569',
              border: tab === t.id ? '1px solid rgba(139,92,246,0.3)' : '1px solid transparent'
            }}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="rounded-2xl p-5" style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)' }}>
        {tab === 'anomalies' && (aiFeatures.anomalies ? <AnomaliesTab/> : <DisabledMsg feature="Anomaly Detection"/>)}
        {tab === 'forecast'  && (aiFeatures.forecast  ? <ForecastTab/>  : <DisabledMsg feature="Asset Forecast"/>)}
      </div>
    </div>
  );
}
